import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import type { CategorySpend } from '@/api/dashboard'
import { ROUTES } from '@/routes/routes'
import { Button } from '@/components/ui/button'
import { CategoryIcon } from '@/lib/categoryIcon'
import { CATEGORICAL_OTHER_COLOR, CATEGORICAL_PALETTE } from '@/lib/categoricalPalette'
import { useIsDarkMode } from '@/hooks/useIsDarkMode'
import { currentMonth } from '@/lib/transactionDateRangeFilter'
import { transactionsLink } from '@/lib/transactionsLink'
import { cn } from '@/lib/utils'
import { SectionLink } from './SectionLink'

const TOP_N = 4

interface Row {
  key: string
  categoryId?: string
  name: string
  icon: string
  total: number
  share: number
  color: string
}

export function SpendingDonut({
  categories,
  monthName,
  format,
  compact,
  onAddTransaction,
  className,
}: {
  categories: CategorySpend[]
  monthName: string
  format: (n: number) => string
  compact: boolean
  onAddTransaction: () => void
  className?: string
}) {
  const isDark = useIsDarkMode()
  const palette = isDark ? CATEGORICAL_PALETTE.dark : CATEGORICAL_PALETTE.light
  const otherColor = isDark ? CATEGORICAL_OTHER_COLOR.dark : CATEGORICAL_OTHER_COLOR.light

  // Categories refunds pushed net-negative aren't a slice of spending.
  const spent = categories.filter((c) => c.total > 0).sort((a, b) => b.total - a.total)
  const total = spent.reduce((sum, c) => sum + c.total, 0)
  const rest = spent.slice(TOP_N)

  const rows: Row[] = spent.slice(0, TOP_N).map((c, i) => ({
    key: c.categoryId ?? c.name,
    categoryId: c.categoryId ?? undefined,
    name: c.name,
    icon: c.icon ?? '',
    total: c.total,
    share: c.total / total,
    color: palette[i],
  }))
  if (rest.length > 0) {
    const restTotal = rest.reduce((sum, c) => sum + c.total, 0)
    rows.push({
      key: 'others',
      name: `${rest.length} other${rest.length === 1 ? '' : 's'}`,
      icon: 'tag',
      total: restTotal,
      share: restTotal / total,
      color: otherColor,
    })
  }

  const stops = rows.map((r, i) => {
    const from = rows.slice(0, i).reduce((sum, prev) => sum + prev.share * 100, 0)
    return `${r.color} ${from}% ${from + r.share * 100}%`
  })
  const pct = (r: Row) => `${Math.round(r.share * 100)}%`
  const donutLabel = `Spending split by category: ${rows.map((r) => `${r.name} ${pct(r)}`).join(', ')}`
  const count = `${spent.length} categor${spent.length === 1 ? 'y' : 'ies'}`

  return (
    <section className={className}>
      <SectionLink
        to={ROUTES.APP.CATEGORIES}
        label="Spending"
        aside={compact && total > 0 ? `${format(total)} · ${count}` : undefined}
      />
      {total <= 0 ? (
        <div className="pb-1 pt-4">
          <p className="mb-3.5 text-[15px] text-[var(--fl-text-2)]">
            Nothing spent yet in {monthName}.
          </p>
          <Button onClick={onAddTransaction} className="h-11 gap-2">
            <Plus size={16} />
            Add transaction
          </Button>
        </div>
      ) : compact ? (
        <div className="mt-1 flex items-center gap-4">
          <Donut rows={rows} stops={stops} label={donutLabel} withShares pct={pct} />
          <ul className="min-w-0 flex-1">
            {rows.map((r) => (
              <li key={r.key} className="flex min-h-8 items-center gap-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: r.color }}
                  aria-hidden="true"
                />
                <CategoryName row={r} className="text-[13px]" />
                <span className="text-[13px] font-semibold tabular-nums text-[var(--fl-heading)]">
                  {format(r.total)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row lg:items-center lg:gap-8">
          <div className="mb-4 mt-1.5 flex shrink-0 items-center gap-5 lg:m-0 lg:flex-col lg:gap-3 lg:text-center">
            <Donut rows={rows} stops={stops} label={donutLabel} pct={pct} />
            <div>
              <p className="font-serif text-[26px] font-bold leading-[30px] text-[var(--fl-heading)]">
                {format(total)}
              </p>
              <p className="mt-1 text-[13px] text-[var(--fl-text-2)]">across {count}</p>
            </div>
          </div>
          <ul className="w-full min-w-0 flex-1">
            {rows.map((r) => (
              <li
                key={r.key}
                className="flex min-h-11 items-center gap-3 border-b border-[var(--fl-divider)] lg:min-h-9"
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                  style={{ color: r.color, backgroundColor: `${r.color}22` }}
                >
                  <CategoryIcon icon={r.icon} size={15} />
                </span>
                <CategoryName row={r} className="text-[15px]" />
                <span className="text-[15px] font-semibold tabular-nums text-[var(--fl-heading)]">
                  {format(r.total)}
                </span>
                <span className="w-10 text-right text-[13px] tabular-nums text-[var(--fl-text-2)]">
                  {pct(r)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

// Slices thinner than this can't hold their share label legibly.
const MIN_LABELLED_SHARE = 0.08
// Distance from the centre to the middle of the ring, as a % of the donut's width (ring: inset 30 of 140px).
const LABEL_RADIUS = (55 / 140) * 100

function Donut({
  rows,
  stops,
  label,
  withShares = false,
  pct,
}: {
  rows: Row[]
  stops: string[]
  label: string
  withShares?: boolean
  pct: (r: Row) => string
}) {
  let from = 0
  return (
    <div
      role="img"
      aria-label={label}
      className={cn(
        'relative shrink-0 rounded-full',
        withShares ? 'h-[140px] w-[140px]' : 'h-28 w-28'
      )}
      style={{ background: `conic-gradient(${stops.join(', ')})` }}
    >
      <div
        className={cn(
          'absolute rounded-full bg-[var(--fl-bg)]',
          withShares ? 'inset-[30px]' : 'inset-[18px]'
        )}
      />
      {withShares &&
        rows.map((r) => {
          const mid = (from + r.share / 2) * 2 * Math.PI
          from += r.share
          if (r.share < MIN_LABELLED_SHARE) return null
          return (
            <span
              key={r.key}
              aria-hidden="true"
              className="absolute -translate-x-1/2 -translate-y-1/2 text-[11px] font-bold tabular-nums text-[var(--fl-primary-fg)] drop-shadow"
              style={{
                left: `${50 + LABEL_RADIUS * Math.sin(mid)}%`,
                top: `${50 - LABEL_RADIUS * Math.cos(mid)}%`,
              }}
            >
              {pct(r)}
            </span>
          )
        })}
    </div>
  )
}

function CategoryName({ row, className }: { row: Row; className: string }) {
  const text = cn('min-w-0 flex-1 truncate text-[var(--fl-text-1)]', className)
  if (row.key !== 'others' && !row.categoryId) return <span className={text}>{row.name}</span>
  return (
    <Link
      to={
        row.categoryId
          ? transactionsLink(
              { month: currentMonth(), categoryId: row.categoryId, type: 'EXPENSE' },
              { back: ROUTES.APP.DASHBOARD }
            )
          : ROUTES.APP.CATEGORIES
      }
      className={cn(text, 'hover:underline')}
    >
      {row.name}
    </Link>
  )
}
